import { axiosInstance } from '@libs/axios/axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAddSnackbarMessage } from '@shared/useAddSnackBar';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { queryClient } from '@shared/queries';
import {
  getReceivedPayments,
  getVisitPaymentQr,
  postSyncReceivedPayments,
  postCreatePendingPayment,
  deleteDeletePendingPayment,
  postConfirmPendingPayment,
} from './api';
import { PaymentQr, QrPaymentType, ReceivedPayment, SyncReceivedPaymentsResult } from './entity';
import { useAnalytics } from '@libs/mixpanel/useAnalytics';

export const RECEIVED_PAYMENTS_QUERY_KEY = ['receivedPayments'];

export const useReceivedPaymentsQuery = (enabled = true, refetchInterval: number | false = false) =>
  useQuery<ReceivedPayment[]>({
    queryKey: RECEIVED_PAYMENTS_QUERY_KEY,
    queryFn: () => getReceivedPayments(),
    enabled,
    refetchInterval,
  });

export const useSyncReceivedPaymentsMutation = () => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations('receivedPayments');

  return useMutation<SyncReceivedPaymentsResult, Error, { silent?: boolean } | undefined>({
    mutationFn: () => postSyncReceivedPayments(),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: RECEIVED_PAYMENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['logs'] });
      queryClient.invalidateQueries({ queryKey: ['bankSyncStatus'] });
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      queryClient.invalidateQueries({ queryKey: ['visit'] });
      if (result.failed) {
        addSnackBarMessage({
          text: 'Část plateb se nepodařilo zpracovat. Zkusíme je automaticky znovu.',
          type: 'error',
        });
        return;
      }
      if (result.skipped) return;
      if (!variables?.silent) {
        addSnackBarMessage({
          text: t('syncSuccess', {
            matched: result.matched,
            fetched: result.fetched,
          }),
          type: 'success',
        });
      }
    },
    onError: (error, variables) => {
      if (!variables?.silent) {
        addSnackBarMessage({ text: error.message || t('syncError'), type: 'error' });
      }
    },
  });
};

export const useVisitPaymentQrQuery = (
  visitId: string | undefined,
  enabled: boolean,
  type: QrPaymentType,
) => {
  const { trackEvent } = useAnalytics();

  return useQuery<PaymentQr>({
    queryKey: ['visitPaymentQr', visitId, type],
    queryFn: async () => {
      const result = await getVisitPaymentQr(visitId as string, type);
      trackEvent('qr_payment_generated', { payment_type: type });
      return result;
    },
    enabled: enabled && !!visitId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreatePendingPaymentMutation = () => {
  const addSnackBarMessage = useAddSnackbarMessage();

  return useMutation<ReceivedPayment, Error, { visitId: string; type: QrPaymentType }>({
    mutationFn: ({ visitId, type }) => postCreatePendingPayment(visitId, type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RECEIVED_PAYMENTS_QUERY_KEY });
    },
    onError: error => {
      addSnackBarMessage({
        text: error.message || 'Nepodařilo se vytvořit čekající platbu.',
        type: 'error',
      });
    },
  });
};

export const useDeletePendingPaymentMutation = () => {
  const addSnackBarMessage = useAddSnackbarMessage();

  return useMutation<void, Error, { visitId: string; type: QrPaymentType }>({
    mutationFn: ({ visitId, type }) => deleteDeletePendingPayment(visitId, type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RECEIVED_PAYMENTS_QUERY_KEY });
    },
    onError: error => {
      addSnackBarMessage({
        text: error.message || 'Nepodařilo se zrušit čekající platbu.',
        type: 'error',
      });
    },
  });
};

export const useConfirmPendingPaymentMutation = () => {
  const addSnackBarMessage = useAddSnackbarMessage();

  return useMutation<void, Error, { visitId: string; type: QrPaymentType }>({
    mutationFn: ({ visitId, type }) => postConfirmPendingPayment(visitId, type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RECEIVED_PAYMENTS_QUERY_KEY });
    },
    onError: error => {
      addSnackBarMessage({
        text: error.message || 'Nepodařilo se ručně potvrdit čekající platbu.',
        type: 'error',
      });
    },
  });
};

export const useBankSyncStatusQuery = () =>
  useQuery({
    queryKey: ['bankSyncStatus'],
    queryFn: async () =>
      (
        await axiosInstance.get<{
          lastSuccessAt: string | null;
          hasError: boolean;
          running: boolean;
        }>('/api/received-payments/sync-status')
      ).data,
    refetchInterval: 30000,
  });
